import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface PaginationBarProps {
  /** 0-based 현재 페이지(PageResult.number 규약). */
  page: number;
  size: number;
  totalElements: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/** 서버 페이징 바 — 총 건수 + 이전/다음 + 페이지 번호(±2). Admin Kit data-table-pagination 의 버튼 규격(size-8). */
export function PaginationBar({
  page,
  size,
  totalElements,
  onPageChange,
  className,
}: PaginationBarProps) {
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = Math.min(totalElements, (page + 1) * size);
  const pages = Array.from({ length: totalPages }, (_, i) => i).filter(
    (p) => Math.abs(p - page) <= 2,
  );

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 px-2 text-sm text-muted-foreground',
        className,
      )}
    >
      <span>
        총 <strong className="font-medium text-foreground">{formatNumber(totalElements)}</strong>건
        중 {from}–{to}
      </span>
      <nav className="flex items-center gap-1" aria-label="페이지 이동">
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          aria-label="이전 페이지"
        >
          <ChevronLeft />
        </Button>
        {pages.map((p) => (
          <Button
            key={p}
            variant={p === page ? 'default' : 'ghost'}
            size="icon"
            className="size-8"
            onClick={() => onPageChange(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p + 1}
          </Button>
        ))}
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          aria-label="다음 페이지"
        >
          <ChevronRight />
        </Button>
      </nav>
    </div>
  );
}
